import { prepareSession, pageSessionHeaders, authenticationFailure } from "./session_recovery"

export function disableSubmitButtons(form) {
  const buttons = Array.from(form.elements).filter(element => element.type === "submit" && !element.disabled)
  const originals = buttons.map(button => [button, button.tagName === "INPUT" ? button.value : button.textContent])
  originals.forEach(([button]) => {
    button.disabled = true
    const label = button.dataset.submitLabel
    if (label) {
      if (button.tagName === "INPUT") button.value = label
      else button.textContent = label
    }
  })
  return () => originals.forEach(([button, label]) => {
    button.disabled = false
    if (!button.dataset.submitLabel) return
    if (button.tagName === "INPUT") button.value = label
    else button.textContent = label
  })
}

// Active Storage intercepts the first submit, uploads files, then clicks the
// submitter again. Let that first event reach its document-level handler.
export async function submitJsonForm(event, callbacks) {
  if (event.defaultPrevented) return
  const form = event.target
  if (form.hasAttribute("data-direct-uploads-processing")) {
    event.preventDefault()
    return
  }
  const uploads = Array.from(form.querySelectorAll('input[type="file"][data-direct-upload-url]'))
  if (uploads.some(input => !input.disabled && input.files.length)) {
    callbacks.saving()
    if (document.querySelector('[data-page-session]') && !form.sessionUploadPrepared) {
      event.preventDefault()
      // This Active Storage version ignores defaultPrevented at document level.
      event.stopPropagation()
      if (form.sessionUploadChecking) return
      form.sessionUploadChecking = true
      try {
        await prepareSession()
        form.sessionUploadPrepared = true
        form.requestSubmit(event.submitter)
      } catch (error) {
        callbacks.error({ session: error.message === "session_expired"
          ? "Sesija nav derīga. Izmaiņas nav saglabātas. Pieslēdzieties vēlreiz un atkārtojiet saglabāšanu."
          : "Neizdevās pārbaudīt sesiju. Izmaiņas nav saglabātas. Pārbaudiet savienojumu un mēģiniet vēlreiz." })
      } finally {
        form.sessionUploadChecking = false
        form.sessionUploadPrepared = false
      }
    }
    return
  }

  event.preventDefault()
  if (form.dataset.fetchSubmitting === "true") return
  form.dataset.fetchSubmitting = "true"
  callbacks.saving()
  const restore = disableSubmitButtons(form)
  form.setAttribute("aria-busy", "true")
  let saved = false
  try {
    await prepareSession()
    if (window.tinymce) window.tinymce.triggerSave()
    form.dispatchEvent(new CustomEvent("form:saving"))
    const body = new FormData(form)
    const submitter = event.submitter
    if (submitter && submitter.name) body.append(submitter.name, submitter.value)
    const csrfMeta = document.querySelector('meta[name="csrf-token"]')
    const response = await fetch(form.action, {
      method: form.method,
      body,
      credentials: "same-origin",
      headers: {
        "Accept": "application/json",
        ...pageSessionHeaders(),
        "X-CSRF-Token": csrfMeta ? csrfMeta.content : ""
      }
    })
    const data = await response.json().catch(() => null)
    if (authenticationFailure(response, data)) {
      callbacks.error({ session: "Sesija nav derīga. Izmaiņas nav saglabātas. Pieslēdzieties vēlreiz tajā pašā kontā un atkārtojiet saglabāšanu." })
      return
    }
    if (response.ok && !response.redirected) {
      callbacks.success(data)
      saved = true
    } else callbacks.error(data)
  } catch (error) {
    callbacks.error(error.message === "session_expired" ? { session: "Sesija nav derīga. Izmaiņas nav saglabātas. Pieslēdzieties vēlreiz un atkārtojiet saglabāšanu." } : null)
  } finally {
    // Keep signed blob IDs for retries and later saves, without uploading the
    // same file twice. A new file selection replaces these hidden inputs.
    uploads.filter(input => input.disabled).forEach(input => {
      input.value = ""
      input.disabled = false
    })
    delete form.dataset.fetchSubmitting
    form.removeAttribute("aria-busy")
    restore()
    if (saved) form.dispatchEvent(new CustomEvent("form:saved"))
  }
}

export function errorMessages(data) {
  return data && typeof data === "object" ? Object.values(data).flat().filter(value => typeof value === "string").join(" ") : ""
}
